'use client';

import { useState, useEffect, useRef } from 'react';
import { sendContactMessage } from '../actions/contact';
import { trackContactForm } from '../../lib/analytics';

declare global {
    interface Window {
        turnstile?: {
            render: (container: string | HTMLElement, options: {
                sitekey: string;
                callback?: (token: string) => void;
                'expired-callback'?: () => void;
                theme?: 'light' | 'dark' | 'auto';
            }) => string;
            reset: (widgetId: string) => void;
        };
    }
}

export default function ContactForm() {
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [message, setMessage] = useState('');
    const turnstileRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

    useEffect(() => {
        if (!siteKey || !turnstileRef.current) return;

        // Wait for Turnstile script to load
        const renderTurnstile = () => {
            if (window.turnstile && turnstileRef.current && !widgetIdRef.current) {
                widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
                    sitekey: siteKey,
                    theme: 'dark',
                });
            }
        };

        // Check if script is already loaded
        if (window.turnstile) {
            renderTurnstile();
        } else {
            // Wait for script to load
            const interval = setInterval(() => {
                if (window.turnstile) {
                    renderTurnstile();
                    clearInterval(interval);
                }
            }, 100);
            return () => clearInterval(interval);
        }
    }, [siteKey]);

    async function handleSubmit(formData: FormData) {
        setStatus('loading');
        const result = await sendContactMessage(formData);

        if (result.success) {
            setStatus('success');
            setMessage('Mesajınız gönderildi! En kısa sürede dönüş yapacağım.');
            trackContactForm('success');
            // Reset form
            const form = document.getElementById('contact-form') as HTMLFormElement;
            form?.reset();
            // Reset Turnstile
            if (window.turnstile && widgetIdRef.current) {
                window.turnstile.reset(widgetIdRef.current);
            }
        } else {
            setStatus('error');
            setMessage(result.error || 'Bir hata oluştu.');
            trackContactForm('error', result.error);
            // Reset Turnstile on error too
            if (window.turnstile && widgetIdRef.current) {
                window.turnstile.reset(widgetIdRef.current);
            }
        }

        // Reset status after 5 seconds
        setTimeout(() => {
            setStatus('idle');
            setMessage('');
        }, 5000);
    }

    return (
        <form id="contact-form" className="contact-form" action={handleSubmit}>
            <div className="form-group">
                <label htmlFor="name">Ad Soyad</label>
                <input type="text" id="name" name="name" placeholder="Adınız Soyadınız" required />
            </div>
            <div className="form-group">
                <label htmlFor="email">E-posta</label>
                <input type="email" id="email" name="email" placeholder="ornek@email.com" required />
            </div>
            <div className="form-group">
                <label htmlFor="message">Mesaj</label>
                <textarea id="message" name="message" rows={4} placeholder="Mesajınız..." required></textarea>
            </div>
            {siteKey && (
                <div
                    ref={turnstileRef}
                    className="cf-turnstile"
                    style={{ marginBottom: '1rem' }}
                />
            )}
            <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%' }}
                disabled={status === 'loading'}
            >
                {status === 'loading' ? 'Gönderiliyor...' : 'Gönder'}
            </button>
            {message && (
                <p style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    background: status === 'success' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                    color: status === 'success' ? '#22c55e' : '#ef4444',
                    textAlign: 'center'
                }}>
                    {message}
                </p>
            )}
        </form>
    );
}
